import { useEffect } from 'react';

import { isNativePlatform } from '@/renderer/utils/isNativePlatform';

/**
 * Hide the native launch splash once the web app has rendered its first frame.
 *
 * The Capacitor SplashScreen plugin is configured `launchAutoHide: false`
 * (capacitor.config.ts), so the branded RAS splash stays up from process launch
 * until we call `hide()` here. That covers the window where the native launch
 * screen has dismissed but the WebView hasn't painted yet — otherwise the bare
 * WebView shows through as a black/blank flash between launch and the app.
 *
 * Runs inside BOTH native shells (iOS and Android); a no-op on web and SSR (the
 * plugin is loaded via dynamic import so it never enters the web bundle graph).
 * `launchAutoHide:false` applies to both platforms, so both need an explicit
 * hide() — gating this to iOS left Android with the splash held up forever and
 * no code path to dismiss it, stranding every Android launch on the splash.
 *
 * Safety: `hide()` is fired on the first painted frame (double rAF) AND from a
 * fallback timeout, both idempotent — a missed or slow first path can never
 * strand the user on the splash.
 */
export default function useHideSplashScreen() {
  useEffect(() => {
    if (!isNativePlatform()) {
      return;
    }

    let hidden = false;
    const hide = async () => {
      if (hidden) {
        return;
      }
      hidden = true;
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
      } catch {
        // A failed hide must never wedge the app on the splash; swallow it.
      }
    };

    // First painted frame: rAF #1 fires before paint, rAF #2 after it.
    const raf = requestAnimationFrame(() => requestAnimationFrame(hide));
    // Fallback: hide regardless if the paint path is slow/never resolves.
    const timer = window.setTimeout(hide, 4000);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, []);
}
